import { CreditCard, Calendar, Clock, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface SubscriptionCardProps {
  subscriptions: {
    id: string;
    status: string;
    planCode: string | null;
    startDate: string | null;
    endDate: string | null;
    isFamilyOwner?: boolean;
    currentFamilySlots?: number;
  }[];
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function getRemainingDaysColor(days: number): string {
  if (days < 7) {
    return 'text-red-500';
  }
  if (days < 30) {
    return 'text-orange-500';
  }
  return 'text-foreground';
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-500/10 text-green-500">Активна</Badge>;
    case 'cancelled':
      return <Badge variant="destructive">Отменена</Badge>;
    case 'expired':
      return <Badge variant="secondary">Истекла</Badge>;
    case 'pending':
      return <Badge className="bg-yellow-500/10 text-yellow-500">Ожидание</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

export function SubscriptionCard({ subscriptions }: SubscriptionCardProps) {
  const active = subscriptions.find((s) => s.status === 'active') ?? null;

  if (!active) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center gap-2 pb-4">
          <CreditCard className="text-muted-foreground h-5 w-5" />
          <CardTitle className="text-base">Подписка</CardTitle>
          <Badge variant="secondary" className="ml-auto">
            Нет активной подписки
          </Badge>
        </CardHeader>
      </Card>
    );
  }

  const endDate = active.endDate ? new Date(active.endDate) : null;
  const remainingDays = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CreditCard className="h-5 w-5" />
          Подписка
        </CardTitle>
        {getStatusBadge(active.status)}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{active.planCode ?? 'Без плана'}</span>
          {active.isFamilyOwner && (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
              <Users className="mr-1 h-3 w-3" />
              Семейная ({active.currentFamilySlots ?? 0} слотов)
            </Badge>
          )}
        </div>
        <div className="text-muted-foreground flex flex-wrap gap-4 text-sm">
          {active.startDate && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(active.startDate)}</span>
              {active.endDate && (
                <>
                  <span>—</span>
                  <span>{formatDate(active.endDate)}</span>
                </>
              )}
            </div>
          )}
          {remainingDays !== null && (
            <div className={`flex items-center gap-1.5 ${getRemainingDaysColor(remainingDays)}`}>
              <Clock className="h-4 w-4" />
              <span>{remainingDays} дней</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
